// @flow
import React, {useRef, useState, Fragment, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import {useForm} from 'react-hook-form';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import {Link} from '@material-ui/core';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import {useLocation} from 'react-router-dom';
import queryString from 'query-string';
import {REGEX} from '../../../constants';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    padding: '20px 0px 20px 0px',
    borderRadius: '10px',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  dialogButtom: {
    backgroundColor: '#7B39FF',
    margin: theme.spacing(5, 0, 3),
    fontWeight: 600,
    borderRadius: '30px',
  },
  dialogContent: {
    color: '#615E6E',
    textAlign: 'center',
    margin: theme.spacing(1, 0, 1),
  },
  dialogForm: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    width: 'fit-content',
  },
  dialogFormNewPassword: {
    textAlign: 'center',
    display: 'block',
    flexDirection: 'column',
    margin: 'auto',
    maxWidth: '380px',
  },
  dialogTitle: {
    textAlign: 'center',
    fontWeight: 600,
    '& h2': {
      fontSize: 26,
    },
  },
  closeIcon: {
    alignSelf: 'flex-end',
    padding: '0px 10px 0px 0px',
  },
  dialogContentText: {
    maxWidth: '80%',
    margin: 'auto',
    textAlign: 'center',
    paddingBottom: '20px',
  },
}));

type Props = {|
  resetPassword: ({token: any, password: string}) => void,
  sendEmailRecovery: ({
    emailRecovery: string,
  }) => void,
  isResettingPassword?: boolean,
  resetPasswordSuccess?: boolean,
  resetPasswordError?: string,
|};

export default function ForgotPassword(props: Props) {
  const classes = useStyles();
  const {
    sendEmailRecovery,
    resetPassword,
    isResettingPassword,
    resetPasswordSuccess,
    resetPasswordError,
  } = props;
  const {handleSubmit, reset, register, errors} = useForm();
  const {
    handleSubmit: handleNewPassword,
    reset: resetNewPassword,
    register: registerNewPassword,
    errors: errorsNewPassword,
    watch: watchNewPassword,
  } = useForm();
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
  const [validateEmailModal, setValidateEmailModal] = useState(false);
  const [newPasswordModal, setNewPasswordModal] = useState(false);
  const location = useLocation();
  const newPassword = useRef({});

  const queryParams = useMemo(() => queryString.parse(location.search), [
    location,
  ]);

  useEffect(() => {
    if (queryParams['auth_recovery_token']) setNewPasswordModal(true);
  }, [queryParams]);

  newPassword.current = watchNewPassword('newPassword', '');

  const handleForgotPassword = event => {
    event.preventDefault();
    setForgotPasswordModal(true);
  };
  const handleForgotPasswordModalClose = () => {
    reset();
    setForgotPasswordModal(false);
  };
  const onSubmitForgotPassword = values => {
    setForgotPasswordModal(false);
    setValidateEmailModal(true);
    sendEmailRecovery({
      emailRecovery: values.emailRecovery,
    });
  };
  const onSubmitNewPassword = values => {
    resetNewPassword();
    setNewPasswordModal(false);
    resetPassword({
      token: queryParams['auth_recovery_token'],
      password: values.newPassword,
    });
  };
  const handleValidateEmailModalClose = () => {
    reset();
    setValidateEmailModal(false);
  };
  const handleNewPasswordModal = () => {
    reset();
    setNewPasswordModal(false);
  };

  const modalForgotPassword = (
    <Fragment>
      <IconButton
        className={classes.closeIcon}
        onClick={() => {
          reset();
          setForgotPasswordModal(false);
        }}
      >
        <i className="material-icons"> close </i>
      </IconButton>
      <DialogTitle className={classes.dialogTitle}>
        ¡No te preocupes!
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>
          Te ayudamos a reestablecer tu contraseña.
        </DialogContentText>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmitForgotPassword)}
          className={classes.dialogForm}
        >
          <TextField
            className={classes.inputForm}
            fullWidth
            id="emailRecovery"
            label="Correo electrónico"
            name="emailRecovery"
            autoComplete="emailRecovery"
            autoFocus
            error={!!errors.emailRecovery}
            helperText={
              errors.emailRecovery
                ? errors.emailRecovery.message
                : 'Introducir el correo registrado para buscar su cuenta'
            }
            inputRef={register({
              required: 'Campo obligatorio',
              pattern: {
                value: REGEX.email,
                message: 'Formato de email inválido',
              },
            })}
            inputProps={{className: classes.innerInputForm}}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            className={classes.dialogButtom}
          >
            Reestablecer contraseña
          </Button>
        </form>
      </DialogContent>
    </Fragment>
  );

  const modalCheckEmail = (
    <Fragment>
      <IconButton
        className={classes.closeIcon}
        onClick={() => {
          reset();
          setValidateEmailModal(false);
        }}
      >
        <i className="material-icons"> close </i>
      </IconButton>
      <DialogTitle className={classes.dialogTitle}>
        Verifica tu correo electrónico
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>
          Si tu cuenta está registrada, te llegará un correo para reestablecer
          tu contraseña.
        </DialogContentText>
        <DialogContentText>
          No te olvides de revisar tu correo SPAM.
        </DialogContentText>
      </DialogContent>
    </Fragment>
  );

  const modalNewPassword = (
    <Fragment>
      <IconButton
        className={classes.closeIcon}
        onClick={() => {
          resetNewPassword();
          setNewPasswordModal(false);
        }}
      >
        <i className="material-icons"> close </i>
      </IconButton>
      <DialogTitle className={classes.dialogTitle}>
        Establecer nueva contraseña
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText className={classes.dialogContentText}>
          Debes elegir una nueva contraseña para acceder a TiendaDa
        </DialogContentText>
        <form
          noValidate
          onSubmit={handleNewPassword(onSubmitNewPassword)}
          className={classes.dialogFormNewPassword}
        >
          <TextField
            className={classes.inputForm}
            fullWidth
            name="newPassword"
            label="Nueva contraseña"
            type="password"
            id="newPassword"
            autoComplete="newPassword"
            error={!!errorsNewPassword.newPassword}
            helperText={
              errorsNewPassword.newPassword &&
              errorsNewPassword.newPassword.message
            }
            inputRef={registerNewPassword({required: 'Campo obligatorio'})}
            inputProps={{className: classes.innerInputForm}}
          />
          <br />
          <TextField
            className={classes.inputForm}
            fullWidth
            name="repeatNewPassword"
            label="Repetir nueva contraseña"
            type="password"
            id="repeatNewPassword"
            autoComplete="repeatNewPassword"
            error={!!errorsNewPassword.repeatNewPassword}
            helperText={
              errorsNewPassword.repeatNewPassword &&
              errorsNewPassword.repeatNewPassword.message
            }
            inputRef={registerNewPassword({
              validate: value =>
                value === newPassword.current || 'Las contraseñas no coinciden',
            })}
            inputProps={{className: classes.innerInputForm}}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            className={classes.dialogButtom}
            disabled={isResettingPassword}
          >
            Guardar
          </Button>
        </form>
      </DialogContent>
    </Fragment>
  );

  return (
    <>
      <SnackBar mode="error" message={resetPasswordError} />
      <SnackBar
        mode="success"
        message={
          resetPasswordSuccess
            ? 'Se guardo la constraseña exitosamente'
            : undefined
        }
      />
      <Container maxWidth="sm">
        <Link
          className={classes.linkText}
          rel="noreferrer noopener"
          href="#"
          onClick={e => handleForgotPassword(e)}
        >
          Olvidé mi contraseña
        </Link>
        <Dialog
          classes={{paper: classes.dialogPaper}}
          maxWidth="sm"
          open={forgotPasswordModal}
          onClose={handleForgotPasswordModalClose}
        >
          {modalForgotPassword}
        </Dialog>
        <Dialog
          classes={{paper: classes.dialogPaper}}
          maxWidth="sm"
          open={validateEmailModal}
          onClose={handleValidateEmailModalClose}
        >
          {modalCheckEmail}
        </Dialog>
        <Dialog
          classes={{paper: classes.dialogPaper}}
          maxWidth="sm"
          open={newPasswordModal}
          onClose={handleNewPasswordModal}
        >
          {modalNewPassword}
        </Dialog>
      </Container>
    </>
  );
}

ForgotPassword.propTypes = {
  resetPassword: PropTypes.func.isRequired,
  sendEmailRecovery: PropTypes.func.isRequired,
  isResettingPassword: PropTypes.bool,
  resetPasswordSuccess: PropTypes.bool,
  resetPasswordError: PropTypes.bool,
};
